import React, { useState, useEffect, useCallback } from 'react';
import { Button, Box } from '@mui/material';
import DialogModal from '../../../components/DialogModal';
import TopLabelTextField from '../../../components/TopLabelTextField/TopLabelTextField';
import { StudentTransactionMatrixItem } from '../../../types/paymentScheme';
import paymentSchemesApis from '../../../apis/payments/paymentSchemes';
import { SnackbarSeverityType, TransactionStatus } from '../../../enums';
import OverlayLoader from '../../../components/OverlayLoader/OverlayLoader';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CustomDatePicker from '../../../components/DatePicker/CustomDatePicker';
import dayjs, { Dayjs } from 'dayjs';

interface CancelPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  transaction: StudentTransactionMatrixItem | null;
  onUpdate: () => void;
  onShowToast: (message: string, severity: SnackbarSeverityType) => void;
}

const CancelPaymentDialog: React.FC<CancelPaymentDialogProps> = ({
  open,
  onClose,
  transaction,
  onUpdate,
  onShowToast
}) => {
  const [receiptNo, setReceiptNo] = useState('');
  const [cancellationRemark, setCancellationRemark] = useState('');
  const [cancellationDate, setCancellationDate] = useState<Dayjs | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const resetState = useCallback(() => {
    setReceiptNo('');
    setCancellationRemark('');
    setCancellationDate(null);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (open) {
      if (transaction) {
        setReceiptNo(transaction.receiptNo || '');
        setCancellationRemark('');
        setCancellationDate(dayjs());
      }
    }
  }, [open, transaction]);

  const handleCancelPayment = async () => {
    if (transaction && cancellationDate) {
      setIsLoading(true);
      try {
        const payload = {
          status: TransactionStatus.CANCELLED,
          remark: cancellationRemark,
          statusChangeDate: cancellationDate.format('YYYY-MM-DDTHH:mm:ss.SSSZ')
        };
        await paymentSchemesApis.cancelPaymentTransaction(receiptNo, payload);
        onShowToast(
          'Payment cancelled successfully!',
          SnackbarSeverityType.success
        );
        onUpdate();
        onClose();
        resetState();
      } catch (error: any) {
        console.error('Error cancelling payment:', error);
        onShowToast(
          error?.errors?.[0]?.message || 'Failed to cancel payment.',
          SnackbarSeverityType.error
        );
      } finally {
        setIsLoading(false);
      }
    } else {
      onShowToast(
        'Please fill in all required fields.',
        SnackbarSeverityType.warning
      );
    }
  };

  const dialogActions = (
    <Button
      variant='contained'
      onClick={handleCancelPayment}
      sx={{ textTransform: 'none' }}
      disabled={!cancellationDate}
    >
      Cancel Payment
    </Button>
  );

  return (
    <DialogModal
      open={open}
      onClose={onClose}
      title='Cancel Payment'
      maxWidth='sm'
      actions={dialogActions}
    >
      {isLoading && <OverlayLoader show={isLoading} />}
      <Box sx={{ mb: 2 }}>
        <TopLabelTextField
          label='Receipt No.'
          value={receiptNo}
          fullWidth
          hasTopLabel
          disabled
        />
      </Box>
      <Box sx={{ mb: 2 }}>
        <TopLabelTextField
          label='Cancellation Remark'
          value={cancellationRemark}
          onChange={(e) => setCancellationRemark(e.target.value)}
          fullWidth
          rows={3}
          hasTopLabel
        />
      </Box>
      <Box sx={{ mb: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <CustomDatePicker
            label='Cancellation Date'
            onChange={(newValue: Dayjs | null) => setCancellationDate(newValue)}
            value={cancellationDate}
          />
        </LocalizationProvider>
      </Box>
    </DialogModal>
  );
};

export default CancelPaymentDialog;
